(() => {
  'use strict';

  const MAX_TRIAL_ITEMS = 5;

  const routes = window.Shopify?.routes?.root || '/';

  const CART_URL = `${routes}cart.js`;
  const ADD_URL = `${routes}cart/add.js`;
  const CHANGE_URL = `${routes}cart/change.js`;

  let cart = null;
  let refreshTimer = null;


  /* ============================================================
     GET CURRENT SHOPIFY CART
  ============================================================ */

  async function getCart() {
    const response = await fetch(CART_URL, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache'
      }
    });

    if (!response.ok) {
      throw new Error('Unable to load Trial Bag.');
    }

    return response.json();
  }


  /* ============================================================
     ADD PRODUCT

     This is retained for any custom [data-add-to-trial] buttons
     you may use elsewhere in the future.

     Your main PDP Add to Trial Bag button is handled by
     product-form.js.
  ============================================================ */

  async function addVariant(variantId, quantity = 1) {
    cart = await getCart();

    if (
      cart.item_count + quantity >
      MAX_TRIAL_ITEMS
    ) {
      showBagFullMessage();
      return false;
    }

    const response = await fetch(ADD_URL, {
      method: 'POST',
      credentials: 'same-origin',

      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },

      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: Number(quantity)
          }
        ]
      })
    });

    if (!response.ok) {
      const error = await response.json();

      throw new Error(
        error.description ||
        'Could not add item to Trial Bag.'
      );
    }

    await refreshTrialBag(true);

    document.dispatchEvent(
      new CustomEvent('trialbag:updated', {
        detail: {
          cart
        }
      })
    );

    return true;
  }


  /* ============================================================
     REMOVE ITEM FROM BOTTOM TRIAL BAG
  ============================================================ */

  async function removeLine(line) {
    const response = await fetch(CHANGE_URL, {
      method: 'POST',
      credentials: 'same-origin',

      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },

      body: JSON.stringify({
        line: Number(line),
        quantity: 0
      })
    });

    if (!response.ok) {
      throw new Error(
        'Could not remove item from Trial Bag.'
      );
    }

    cart = await response.json();

    updateAllTrialBagUI(cart);

    document.dispatchEvent(
      new CustomEvent('trialbag:updated', {
        detail: {
          cart
        }
      })
    );
  }


  /* ============================================================
     UPDATE EVERYTHING
  ============================================================ */

  function updateAllTrialBagUI(cartData) {
    if (!cartData) return;

    updateHeaderCounter(cartData.item_count);

    renderTrialBag(cartData);

    updateProductButtons(cartData);
  }


  /* ============================================================
     RENDER BOTTOM TRIAL BAG

     Important:
     Bottom Trial Bag only exists on collection/product pages.
     Header still updates even if this element doesn't exist.
  ============================================================ */

  function renderTrialBag(cartData) {
    const bag =
      document.querySelector('[data-trial-bag]');

    if (!bag) return;

    const count = cartData.item_count;


    /* EMPTY STATE */

    bag.classList.toggle(
      'trial-bag--empty',
      count === 0
    );


    /* COUNTERS */

    bag
      .querySelectorAll('[data-trial-count]')
      .forEach((element) => {
        element.textContent = count;
      });

    bag
      .querySelectorAll('[data-selected-count]')
      .forEach((element) => {
        element.textContent = count;
      });


    /* PROGRESS */

    bag
      .querySelectorAll('[data-progress]')
      .forEach((element) => {
        const position =
          Number(element.dataset.progress);

        element.classList.toggle(
          'is-active',
          position <= count
        );
      });


    /* HELPER TEXT */

    const helper =
      bag.querySelector('[data-trial-helper]');

    if (helper) {
      const remaining =
        MAX_TRIAL_ITEMS - count;

      if (count === 0) {
        helper.textContent =
          'Add up to 5 items to try at home.';
      }

      else if (remaining > 0) {
        helper.textContent =
          `Add up to ${remaining} more item${remaining === 1 ? '' : 's'} to try.`;
      }

      else {
        helper.textContent =
          'Your Trial Bag is full.';
      }
    }


    /* PRODUCT THUMBNAILS */

    const itemsContainer =
      bag.querySelector('[data-trial-items]');

    if (!itemsContainer) return;

    itemsContainer.innerHTML = '';

    cartData.items
      .slice(0, MAX_TRIAL_ITEMS)
      .forEach((item, index) => {

        const itemElement =
          document.createElement('div');

        itemElement.className =
          'trial-bag__item';

        itemElement.dataset.line =
          index + 1;


        const image =
          item.featured_image?.url ||
          item.image ||
          '';


        const title =
          item.product_title ||
          item.title ||
          'Product';


        itemElement.innerHTML = `
          ${
            image
              ? `
                <img
                  src="${escapeHTML(image)}"
                  alt="${escapeHTML(title)}"
                  loading="lazy"
                >
              `
              : ''
          }

          <button
            type="button"
            class="trial-bag__remove"
            data-remove-line="${index + 1}"
            aria-label="Remove ${escapeHTML(title)}"
          >
            ×
          </button>
        `;

        itemsContainer.appendChild(
          itemElement
        );
      });


    /* ADD MORE */

    if (count < MAX_TRIAL_ITEMS) {
      const addMore =
        document.createElement('button');

      addMore.type = 'button';

      addMore.className =
        'trial-bag__add-more';

      addMore.dataset.addMore = '';

      addMore.innerHTML = `
        <span>+</span>
        Add more
      `;

      itemsContainer.appendChild(addMore);
    }
  }


  /* ============================================================
     HEADER TRIAL BAG
  ============================================================ */

  function updateHeaderCounter(count) {
    document
      .querySelectorAll(
        '[data-header-trial-count]'
      )
      .forEach((element) => {

        element.textContent =
          `${count}/${MAX_TRIAL_ITEMS}`;

      });


    document
      .querySelectorAll(
        '.header-trial-bag__progress'
      )
      .forEach((progress) => {

        progress
          .querySelectorAll('span')
          .forEach((segment, index) => {

            segment.classList.toggle(
              'is-active',
              index < count
            );

          });

      });
  }


  /* ============================================================
     PDP BUTTON STATE
  ============================================================ */

  function updateProductButtons(cartData) {
    const full =
      cartData.item_count >=
      MAX_TRIAL_ITEMS;


    /*
     * Supports:
     *
     * data-trial-product-submit
     * AND
     * normal Shopify product form buttons
     */

    const buttons =
      document.querySelectorAll(
        `
          [data-trial-product-submit],
          product-form button[name="add"]
        `
      );


    buttons.forEach((button) => {
      const textElement =
        button.querySelector('span');


      /*
       * Don't overwrite Shopify's Sold Out state.
       */

      const soldOutMessage =
        button.querySelector(
          '.sold-out-message:not(.hidden)'
        );

      if (soldOutMessage) return;


      if (full) {
        button.setAttribute(
          'aria-disabled',
          'true'
        );

        button.classList.add(
          'trial-bag-full'
        );

        if (textElement) {
          textElement.textContent =
            'Trial Bag Full';
        }

      }

      else {
        /*
         * Only clear the disabled state if
         * we're the reason it was disabled.
         */

        if (
          button.classList.contains(
            'trial-bag-full'
          )
        ) {
          button.removeAttribute(
            'aria-disabled'
          );
        }

        button.classList.remove(
          'trial-bag-full'
        );

        if (textElement) {
          textElement.textContent =
            'Add to Trial Bag';
        }
      }
    });
  }


  /* ============================================================
     FULL BAG MESSAGE
  ============================================================ */

  function showBagFullMessage() {
    let toast =
      document.querySelector(
        '.trial-bag-toast'
      );


    if (!toast) {
      toast =
        document.createElement('div');

      toast.className =
        'trial-bag-toast';

      document.body.appendChild(toast);
    }


    toast.textContent =
      'Your Trial Bag is full. Remove an item to try something else.';

    toast.classList.add(
      'is-visible'
    );


    window.clearTimeout(
      toast.hideTimeout
    );


    toast.hideTimeout =
      window.setTimeout(() => {

        toast.classList.remove(
          'is-visible'
        );

      }, 3000);
  }


  /* ============================================================
     ESCAPE HTML
  ============================================================ */

  function escapeHTML(value = '') {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }


  /* ============================================================
     REFRESH FROM SHOPIFY

     immediate = true:
     refresh immediately.

     otherwise:
     small debounce prevents duplicate Shopify/pubsub/custom
     events from causing several cart requests.
  ============================================================ */

  async function refreshTrialBag(
    immediate = false
  ) {
    window.clearTimeout(
      refreshTimer
    );


    if (immediate) {
      try {
        cart = await getCart();

        updateAllTrialBagUI(cart);

      } catch (error) {
        console.error(
          'Trial Bag refresh failed:',
          error
        );
      }

      return;
    }


    refreshTimer =
      window.setTimeout(async () => {

        try {
          cart = await getCart();

          updateAllTrialBagUI(cart);

        } catch (error) {
          console.error(
            'Trial Bag refresh failed:',
            error
          );
        }

      }, 80);
  }


  /* ============================================================
     CUSTOM EVENTS
  ============================================================ */

  document.addEventListener(
    'trialbag:refresh',
    () => {
      refreshTrialBag();
    }
  );


  document.addEventListener(
    'trialbag:limit',
    () => {
      showBagFullMessage();
    }
  );


  /* ============================================================
     SHOPIFY PUB/SUB CART UPDATES

     Handles:
     - PDP additions
     - cart drawer removal
     - cart page removal
     - quantity changes
     - other Dawn cart updates
  ============================================================ */

  function subscribeToShopifyCartUpdates() {
    if (
      typeof window.subscribe === 'function' &&
      window.PUB_SUB_EVENTS?.cartUpdate
    ) {
      window.subscribe(
        window.PUB_SUB_EVENTS.cartUpdate,
        () => {
          refreshTrialBag();
        }
      );

      return;
    }


    /*
     * In Dawn these functions are often globals,
     * but not necessarily properties on window.
     */

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        () => {
          refreshTrialBag();
        }
      );
    }
  }


  /* ============================================================
     CLICK EVENTS
  ============================================================ */

  document.addEventListener(
    'click',
    async (event) => {


      /* CUSTOM ADD TO TRIAL BUTTON */

      const addButton =
        event.target.closest(
          '[data-add-to-trial]'
        );


      if (addButton) {
        event.preventDefault();

        if (
          addButton.disabled ||
          addButton.getAttribute(
            'aria-disabled'
          ) === 'true'
        ) {
          return;
        }


        const variantId =
          addButton.dataset.variantId;

        if (!variantId) return;


        try {
          addButton.disabled = true;

          const originalText =
            addButton.textContent;

          addButton.textContent =
            'Adding…';


          const added =
            await addVariant(
              variantId,
              1
            );


          addButton.disabled = false;

          if (added) {
            addButton.textContent =
              'Added ✓';

            setTimeout(() => {
              addButton.textContent =
                originalText ||
                'Add to Trial Bag';
            }, 1000);
          }

          else {
            addButton.textContent =
              originalText ||
              'Add to Trial Bag';
          }

        } catch (error) {
          console.error(
            'Trial Bag add failed:',
            error
          );

          addButton.disabled = false;

          addButton.textContent =
            'Add to Trial Bag';
        }

        return;
      }


      /* REMOVE FROM BOTTOM BAG */

      const removeButton =
        event.target.closest(
          '[data-remove-line]'
        );


      if (removeButton) {
        event.preventDefault();

        if (
          removeButton.getAttribute(
            'aria-busy'
          ) === 'true'
        ) {
          return;
        }


        try {
          removeButton.setAttribute(
            'aria-busy',
            'true'
          );

          await removeLine(
            removeButton.dataset.removeLine
          );

        } catch (error) {
          console.error(
            'Trial Bag removal failed:',
            error
          );

          removeButton.removeAttribute(
            'aria-busy'
          );
        }

        return;
      }


      /* ADD MORE */

      const addMore =
        event.target.closest(
          '[data-add-more]'
        );


      if (addMore) {
        event.preventDefault();


        const collectionGrid =
          document.querySelector(
            '#product-grid'
          );


        if (collectionGrid) {
          collectionGrid.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });

          return;
        }


        window.location.href =
          `${routes}collections/all`;
      }
    }
  );


  /* ============================================================
     TAB / WINDOW RETURNS

     Useful if cart changed in another tab.
  ============================================================ */

  document.addEventListener(
    'visibilitychange',
    () => {
      if (
        document.visibilityState ===
        'visible'
      ) {
        refreshTrialBag();
      }
    }
  );


  window.addEventListener(
    'pageshow',
    () => {
      refreshTrialBag();
    }
  );


  /* ============================================================
     INITIALIZE
  ============================================================ */

  async function init() {
    try {
      cart = await getCart();

      updateAllTrialBagUI(cart);

      subscribeToShopifyCartUpdates();

    } catch (error) {
      console.error(
        'Trial Bag initialization failed:',
        error
      );
    }
  }


  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );
  }

  else {
    init();
  }

})();